import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Chip, Stack, Typography } from '@mui/material';
import React from 'react';

// A sandbox tenant's delivery statuses (backend SBX plan D5; dev-platform S4a1/S4b, shown in the console
// by S4c, here by S7b): event 13-15 (Event_Delivery_Log / Event_Delivery_Attempt) and method 6-8
// (Event_Delivery_Notification_Log / Notification_Delivery_Attempt_Method). Sent to allowlisted testers
// only, partly sent and partly captured, or captured in the sandbox's outbox. Expected outcomes, never
// errors: shown neutral with the flask the console uses. A production tenant never has them, so
// production rows render exactly as before.

export const SANDBOX_STATUS_LABEL = {
    sentAllowlisted: 'Sent (allowlisted)',
    partiallySentAllowlisted: 'Partially sent (allowlisted)',
    captured: 'Captured (sandbox)',
};

export const EVENT_SENT_ALLOWLISTED = 13;
export const EVENT_PARTIALLY_SENT_ALLOWLISTED = 14;
export const EVENT_CAPTURED = 15;

export const METHOD_SENT_ALLOWLISTED = 6;
export const METHOD_PARTIALLY_SENT_ALLOWLISTED = 7;
export const METHOD_CAPTURED = 8;

const SANDBOX_EVENT = {
    [EVENT_SENT_ALLOWLISTED]: SANDBOX_STATUS_LABEL.sentAllowlisted,
    [EVENT_PARTIALLY_SENT_ALLOWLISTED]: SANDBOX_STATUS_LABEL.partiallySentAllowlisted,
    [EVENT_CAPTURED]: SANDBOX_STATUS_LABEL.captured,
};

const SANDBOX_METHOD = {
    [METHOD_SENT_ALLOWLISTED]: SANDBOX_STATUS_LABEL.sentAllowlisted,
    [METHOD_PARTIALLY_SENT_ALLOWLISTED]: SANDBOX_STATUS_LABEL.partiallySentAllowlisted,
    [METHOD_CAPTURED]: SANDBOX_STATUS_LABEL.captured,
};

/** A sandbox event status's words (13-15), or null for any other status. */
export const sandboxEventLabel = (status) => SANDBOX_EVENT[status] ?? null;

/** A sandbox method status's words (6-8), or null for any other status. */
export const sandboxMethodLabel = (status) => SANDBOX_METHOD[status] ?? null;

/** "3 captured", or null when nothing was captured (every production row). */
export const capturedCount = (n) => (n ? `${n} captured` : null);

/** A method chip's counts: a captured method sent nothing, so it reads "captured", not "0/0". */
export const methodCounts = (status, nSuccess, nError) => (
    status === METHOD_CAPTURED ? 'captured' : `${nSuccess}/${nError}`
);

/** The counts line over a list of events: "2 sent (allowlisted) · 1 partially sent · 5 captured" --
 *  null when none of them is a sandbox's (production lists show no line). */
export const sandboxCountsLine = (statuses) => {
    const n = { [EVENT_SENT_ALLOWLISTED]: 0, [EVENT_PARTIALLY_SENT_ALLOWLISTED]: 0, [EVENT_CAPTURED]: 0 };
    statuses.forEach((s) => {
        if (s in n) n[s] += 1;
    });
    const parts = [
        n[EVENT_SENT_ALLOWLISTED] && `${n[EVENT_SENT_ALLOWLISTED]} sent (allowlisted)`,
        n[EVENT_PARTIALLY_SENT_ALLOWLISTED] && `${n[EVENT_PARTIALLY_SENT_ALLOWLISTED]} partially sent`,
        n[EVENT_CAPTURED] && `${n[EVENT_CAPTURED]} captured`,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
};

/** An event's status cell: a sandbox status is a neutral outlined chip with the flask; any other
 *  status is the plain text it always was. `nCaptured` adds "N captured" beneath. */
export const EventStatusCell = ({ status, fallback, nCaptured }) => {
    const sandbox = sandboxEventLabel(status);
    const captured = capturedCount(nCaptured);
    if (!sandbox && !captured) {
        return <>{fallback}</>;
    }
    return (
        <Stack spacing={0.25} alignItems="flex-start">
            {sandbox
                ? <Chip size="small" variant="outlined" icon={<ScienceOutlinedIcon sx={{ fontSize: 14 }} />} label={sandbox} data-testid="sandbox-event-status" />
                : <span>{fallback}</span>}
            {captured && <Typography variant="caption" color="text.secondary">{captured}</Typography>}
        </Stack>
    );
};
